import { z } from 'zod';

import type { W_JobMaterial_FindTable_VM_Single } from 'ab-api/web/jobMaterial/findTable';

import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { ConstrainedUnitEnum, PlainUnitList } from '@acceligentllc/shared/enums/quantityUnit';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

const unitEnumSchema = z.enum([
	...PlainUnitList,
	ConstrainedUnitEnum.PCT,
]);

export const JobMaterialFMBaseCostHistorySchema = z.object({
	validFrom: z.string().refine((_val) => TimeUtils.isDateInCorrectFormat(_val, TimeFormat.DB_DATE_ONLY)),
	baseCost: z.coerce.number<string>().gt(0, 'Base Cost must be greater than 0.'),
	shippingMarkup: z.string().transform((_val) => _val === '' ? null : _val).pipe(z.coerce.number<string>().nonnegative('Shipping markup can not be negative.').nullable()).nullable(),
	salesMarkup: z.string().transform((_val) => _val === '' ? null : _val).pipe(z.coerce.number<string>().nonnegative('Sales Markup can not be negative.').nullable()).nullable(),
	tax: z.string().transform((_val) => _val === '' ? null : _val).pipe(z.coerce.number<string>().nonnegative('Tax can not be negative.').nullable()).nullable(),
	jobShippingMarkup: z.number(),
	jobSalesMarkup: z.number(),
	jobTax: z.number(),
	totalCost: z.number(),
});

export const JobMaterialFMSchema = z.object({
	id: z.string(),
	jobMaterialId: z.number(),
	materialId: z.number(),
	group1: z.string().min(1, 'Group 1 is required'),
	group2: z.string().nullable(),
	group3: z.string().nullable(),
	tags: z.string(),
	itemNumber: z.coerce.number<string>(),
	vendorId: z.number().nullable(),
	vendorUniqueId: z.string().nullable(),
	vendorName: z.string().nullable(),
	displayName: z.string(),
	description: z.string(),
	unit: unitEnumSchema,
	currentRate: JobMaterialFMBaseCostHistorySchema,
	futureRate: JobMaterialFMBaseCostHistorySchema.nullable(),
});

export const JobMaterialForRemoveSchema = z.object({
	jobMaterialId: z.number(),
});

export const JobMaterialFormSchema = z.object({
	materials: z.array(JobMaterialFMSchema),
	materialsForRemove: z.array(JobMaterialForRemoveSchema),
});
export type JobMaterialFM = z.infer<typeof JobMaterialFMSchema>;

export type JobMaterialFMInput = z.input<typeof JobMaterialFMSchema>;

export type JobMaterialsFormInput = z.input<typeof JobMaterialFormSchema>;

export type JobMaterialsFormOutput = z.infer<typeof JobMaterialFormSchema>;

export const jobMaterialVMtoFMInput = (_mat: W_JobMaterial_FindTable_VM_Single): JobMaterialFMInput => {
	return {
		id: _mat.materialId.toString(),
		jobMaterialId: _mat.jobMaterialId,
		materialId: _mat.materialId,
		displayName: _mat.displayName,
		group1: _mat.materialGroups[0] ?? '',
		group2: _mat.materialGroups[1] ?? null,
		group3: _mat.materialGroups[2] ?? null,
		tags: _mat.tags.join(','),
		vendorId: _mat.vendorId,
		vendorUniqueId: _mat.vendorUniqueId,
		vendorName: _mat.vendor,
		itemNumber: _mat.itemNumber.toString(),
		description: _mat.description,
		unit: _mat.unit,
		currentRate: {
			validFrom: _mat.currentRate.validFrom,
			baseCost: _mat.currentRate.baseCost.toString(),
			shippingMarkup: _mat.currentRate.shippingMarkup?.toString() ?? null,
			salesMarkup: _mat.currentRate.salesMarkup?.toString() ?? null,
			tax: _mat.currentRate.tax?.toString() ?? null,
			jobShippingMarkup: _mat.currentRate.jobShippingMarkup,
			jobSalesMarkup: _mat.currentRate.jobSalesMarkup,
			jobTax: _mat.currentRate.jobTax,
			totalCost: _mat.currentRate.totalCost,
		},
		futureRate: _mat.futureRate ? {
			validFrom: _mat.futureRate.validFrom,
			baseCost: _mat.futureRate.baseCost.toString(),
			shippingMarkup: _mat.futureRate.shippingMarkup?.toString() ?? null,
			salesMarkup: _mat.futureRate.salesMarkup?.toString() ?? null,
			tax: _mat.futureRate.tax?.toString() ?? null,
			jobShippingMarkup: _mat.futureRate.jobShippingMarkup,
			jobSalesMarkup: _mat.futureRate.jobSalesMarkup,
			jobTax: _mat.futureRate.jobTax,
			totalCost: _mat.futureRate.totalCost,
		} : null,
	};
};

export const createJobMaterialsForm = (materials: W_JobMaterial_FindTable_VM_Single[]): JobMaterialsFormInput => {
	return {
		materials: materials.map(jobMaterialVMtoFMInput),
		materialsForRemove: [],
	};
};
